/**
 * Deck construction.
 *
 * `buildDeck` takes a DeckSpec rather than baking the composition in, so a
 * different deck is a value to pass, not a code change. It refuses to build
 * one whose counts don't sum to the declared total, which turns a silent
 * miscount into a startup error.
 *
 * config/deck.yml is the same composition in readable form, with the sources
 * and the reasoning. Nothing loads it - the server would need a YAML parser
 * and a file read on a path that differs per deploy - so DEFAULT_DECK_SPEC
 * below and that file have to be changed together. The per-type assertions in
 * rules.test.ts are what stop them drifting apart unnoticed.
 */

import { COLORS, type Card, type CardKind, type Color } from './types.js';

export interface DeckSpec {
  total: number;
  colors: readonly Color[];
  perColor: {
    /**
     * Copies of the 0 in each colour. Standard UNO has one; No Mercy has two,
     * like every other rank. Kept separate from `numbers` so a deck.yml can
     * express either.
     */
    zero: number;
    /** Copies of EACH rank 1-9. */
    numbers: number;
    drawTwo: number;
    skip: number;
    reverse: number;
    /** The COLOURED +4. Distinct from Wild Reverse Draw 4. */
    drawFour: number;
    skipEveryone: number;
    discardAll: number;
  };
  /**
   * Wild counts. Partial on purpose: No Mercy has neither a plain Wild nor a
   * plain Wild Draw 4, so those keys are simply absent. The kinds still exist
   * in the type, so a custom deck.yml can add them back.
   */
  wilds: Partial<{
    wild: number;
    wildDrawFour: number;
    wildDrawSix: number;
    wildDrawTen: number;
    wildReverseDrawFour: number;
    wildColorRoulette: number;
  }>;
}

/**
 * Default composition. Mirrors config/deck.yml.
 *
 *   38 per colour x 4 = 152, plus 16 wilds = 168.
 *
 * Verified against Mattel's own instruction sheet for HWV18 (linked in the
 * README), not against a rules-aggregator site — several of those disagree
 * with each other and two of them publish breakdowns that do not sum to 168.
 *
 * Note what is NOT here:
 *
 *   - a plain **Wild**. Every wild in No Mercy has teeth. The instruction
 *     sheet's own scoring table lists exactly four wild cards, and a plain
 *     Wild is not among them.
 *   - a plain **Wild Draw 4**. There is a COLOURED +4 in each suit and a
 *     Wild Reverse Draw 4, and no colourless plain +4.
 */
export const DEFAULT_DECK_SPEC: DeckSpec = {
  total: 168,
  colors: COLORS,
  perColor: {
    zero: 2,
    numbers: 2, // each of ranks 1-9
    drawTwo: 3,
    skip: 3,
    reverse: 3,
    drawFour: 3,
    skipEveryone: 3,
    discardAll: 3,
  },
  wilds: {
    wildDrawSix: 4,
    wildDrawTen: 4,
    wildReverseDrawFour: 4,
    wildColorRoulette: 4,
  },
};

export class DeckSpecError extends Error {}

/** Cards a spec will produce, without building them. Used for validation and by bots. */
export function deckSize(spec: DeckSpec): number {
  const p = spec.perColor;
  // ranks 1-9 at p.numbers each, plus however many zeros.
  const perColor =
    p.zero +
    p.numbers * 9 +
    p.drawTwo +
    p.skip +
    p.reverse +
    p.drawFour +
    p.skipEveryone +
    p.discardAll;
  const wilds = Object.values(spec.wilds).reduce((a, b) => a + (b ?? 0), 0);
  return perColor * spec.colors.length + wilds;
}

/**
 * Build the full deck in canonical (unshuffled) order.
 *
 * Throws if the spec doesn't sum to `total` — this is the guard that makes a
 * miscounted deck.yml fail loudly at startup instead of quietly skewing the
 * game for weeks.
 */
export function buildDeck(spec: DeckSpec = DEFAULT_DECK_SPEC): Card[] {
  const actual = deckSize(spec);
  if (actual !== spec.total) {
    throw new DeckSpecError(
      `Deck composition does not match declared total: counts sum to ${actual}, ` +
        `but total is ${spec.total}. Fix config/deck.yml (see Task 0 in the README).`,
    );
  }

  const cards: Card[] = [];
  let n = 0;
  const push = (kind: CardKind, color?: Color, rank?: number) => {
    cards.push({ id: `c${n++}`, kind, ...(color ? { color } : {}), ...(rank !== undefined ? { rank } : {}) });
  };

  for (const color of spec.colors) {
    for (let i = 0; i < spec.perColor.zero; i++) push('number', color, 0);
    for (let rank = 1; rank <= 9; rank++) {
      for (let i = 0; i < spec.perColor.numbers; i++) push('number', color, rank);
    }
    for (let i = 0; i < spec.perColor.drawTwo; i++) push('drawTwo', color);
    for (let i = 0; i < spec.perColor.skip; i++) push('skip', color);
    for (let i = 0; i < spec.perColor.reverse; i++) push('reverse', color);
    for (let i = 0; i < spec.perColor.drawFour; i++) push('drawFour', color);
    for (let i = 0; i < spec.perColor.skipEveryone; i++) push('skipEveryone', color);
    for (let i = 0; i < spec.perColor.discardAll; i++) push('discardAll', color);
  }

  for (const [kind, count] of Object.entries(spec.wilds) as [CardKind, number | undefined][]) {
    for (let i = 0; i < (count ?? 0); i++) push(kind);
  }

  return cards;
}
